// Job search results → compact status-badged cards, plus the ICP card that
// explains what an upcoming candidate match is scored against.

import type { BlockOutput, IcpBlockData, JobCardData } from '../../types';
import { pluralise } from '../pluralise';
import { arr, str, toId } from './guards';
import { colReader, mapSuggestedActions } from './shared';
import type { StructuredIcp, StructuredTable } from './types';

// Nexus dates arrive as ISO strings or epoch seconds/millis; render a short
// human date ("12 Jan 2026") or nothing when unparsable.
const formatJobDate = (v?: string | number): string | undefined => {
  if (v === undefined || v === null || v === '') return undefined;
  let d: Date;
  if (typeof v === 'number' || /^\d+$/.test(String(v))) {
    const n = Number(v);
    // Zero/negative epochs are Go zero-values passed through, not dates.
    if (n <= 0) return undefined;
    d = new Date(n < 1e12 ? n * 1000 : n);
  } else {
    d = new Date(String(v));
  }
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const buildJobListBlock = (
  t: StructuredTable,
  toolCallId?: string
): BlockOutput => {
  const rows = t.rows ?? [];
  const ids = t.ids ?? [];
  const hrefs = t.hrefs ?? [];
  const metas = t.row_meta ?? [];
  const total = t.total ?? rows.length;
  const col = colReader(t.columns);
  const jobs: JobCardData[] = rows.map((r, i) => {
    const m = metas[i] ?? {};
    return {
      id: ids[i] ?? String(i),
      // Backend job columns: ["ID","Job","Department","Location","Workplace",
      // "Employment type","Status"] — row_meta carries the rest of the nexus
      // job document (code, confidential, open_at/created_at).
      title: col(r, 'Job') || col(r, 'Name') || `#${ids[i] ?? i}`,
      status: col(r, 'Status'),
      department: col(r, 'Department'),
      office: col(r, 'Office') || col(r, 'Location'),
      workplace: col(r, 'Workplace') || m.workplace,
      employmentType:
        col(r, 'Employment type') ||
        col(r, 'Employment') ||
        m.employment_type ||
        m.emp_type,
      code: m.code || undefined,
      confidential: m.confidential === true,
      hiringTeam: Array.isArray(m.hiring_team_names)
        ? m.hiring_team_names.filter(Boolean).map(String)
        : undefined,
      openedAt: formatJobDate(m.open_at),
      createdAt: formatJobDate(m.created_at),
      // hrefs are internal ATS routes (/jobs/<id>) that core-web serves.
      url: hrefs[i] || undefined,
    };
  });
  const summary =
    total > rows.length
      ? `${pluralise(total, 'job')} — showing top ${rows.length}.`
      : `${pluralise(total, 'job')}.`;
  return {
    type: 'jobList',
    data: {
      summary,
      jobs,
      ids: ids.map(String),
      suggestedActions: mapSuggestedActions(t.suggested_actions),
      // Selection state (checked jobs) is keyed by this in the store.
      toolCallId,
    },
  };
};

// Ideal-candidate profile from kula_job_match_candidates — the criteria the
// upcoming candidate search matches against, shown so the recruiter sees WHAT
// drove the match without relying on the assistant relaying it in prose.
export const buildIcpBlock = (
  t: StructuredIcp,
  toolCallId?: string
): BlockOutput | null => {
  const criteria = arr(t.criteria)
    .filter((c): c is string => typeof c === 'string' && c.trim() !== '')
    .slice(0, 24);
  const summary = str(t.summary)?.trim() ? str(t.summary) : undefined;
  if (criteria.length === 0 && !summary) return null;
  const data: IcpBlockData = {
    jobId: toId(t.job_id),
    jobTitle: str(t.job_title),
    source: str(t.source),
    summary,
    criteria,
    toolCallId,
  };
  return { type: 'icp', data };
};
